import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HiArrowsRightLeft } from "react-icons/hi2";
import { FaBus, FaCalendarAlt, FaSearch, FaMapMarkerAlt, FaChevronRight, FaShieldAlt, FaTicketAlt, FaClock } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";

const BusBookingHero = () => {
  const navigate = useNavigate();
  const [tripType, setTripType] = useState("One Way");
  const [isScrolled, setIsScrolled] = useState(false);

  // Search States
  const [sourceSearch, setSourceSearch] = useState({ query: "", results: [] });
  const [destSearch, setDestSearch] = useState({ query: "", results: [] });

  // Final Form State
  const [formData, setFormData] = useState({
    sourceCity: null,
    destCity: null,
    travelDate: "",
    returnDate: "",
  });

  useEffect(() => {
    if (window.flatpickr) {
      window.flatpickr(".bus-date-input", {
        dateFormat: "Y-m-d",
        minDate: "today",
        disableMobile: "true",
        onChange: (selectedDates, dateStr, instance) => {
          const fieldName = instance.element.getAttribute("name");
          setFormData(prev => ({ ...prev, [fieldName]: dateStr }));
        }
      });
    }
  }, [tripType]);

  const handleCitySearch = async (query, type) => {
    const setter = type === "source" ? setSourceSearch : setDestSearch;
    setter(prev => ({ ...prev, query }));
    if (query.length > 2) {
      try {
        const res = await axios.get(`${baseurl}/getSeatSellerCitiesFromDB?query=${query}`);
        if (res.data.status === "success") {
          setter(prev => ({ ...prev, results: res.data.cities || [] }));
        }
      } catch (err) { console.error(err); }
    } else {
      setter(prev => ({ ...prev, results: [] }));
    }
  };

  const selectCity = (city, type) => {
    if (type === "source") {
      setFormData(prev => ({ ...prev, sourceCity: city }));
      setSourceSearch({ query: city.name, results: [] });
    } else {
      setFormData(prev => ({ ...prev, destCity: city }));
      setDestSearch({ query: city.name, results: [] });
    }
  };

  const swapCities = () => {
    const { sourceCity, destCity } = formData;
    setFormData(prev => ({ ...prev, sourceCity: destCity, destCity: sourceCity }));
    setSourceSearch({ query: destCity?.name || "", results: [] });
    setDestSearch({ query: sourceCity?.name || "", results: [] });
  };

  const onSearchBuses = () => {
    const { sourceCity, destCity, travelDate, returnDate } = formData;
    if (!sourceCity || !destCity || !travelDate) {
      Swal.fire({ icon: 'warning', title: 'Missing Info', text: 'Please fill all fields' });
      return;
    }
    const queryStr = new URLSearchParams({
      sourceId: sourceCity.city_id,
      sourceName: sourceCity.name,
      destId: destCity.city_id,
      destName: destCity.name,
      date: travelDate,
      returnDate: tripType === "Roundtrip" ? returnDate : ""
    }).toString();
    navigate(`/bus-result?${queryStr}`);
  };

  return (
    <>
      <style>{`
        .hero-section {
          position: relative;
          max-height: 400px;
          background: linear-gradient(rgba(11, 61, 71, 0.8), rgba(22, 96, 112, 0.9)), 
                      url('https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&q=80&w=2000');
          background-size: cover;
          background-position: center;
          padding: 150px 0 150px 0;
          color: white;
          text-align: center;
        }

        .hero-content h1 {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 15px;
          text-shadow: 0 2px 10px rgba(0,0,0,0.2);
        }

        .hero-content p {
          font-size: 1.2rem;
          opacity: 0.9;
          margin-bottom: 40px;
        }

        .floating-booking-container {
          max-width: 1100px;
          margin: -80px auto 50px auto;
          position: relative;
          z-index: 10;
          padding: 0 15px;
        }

        .bus-form-card {
          background: #ffffff;
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 15px 40px rgba(0,0,0,0.12);
        }

        .trip-type-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
        }

        .tab-btn {
          background: #f1f5f9;
          border: none;
          padding: 8px 20px;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          color: #64748b;
          transition: 0.3s;
        }

        .tab-btn.active {
          background: #166070;
          color: white;
        }

        .booking-grid {
          display: grid;
          grid-template-columns: 1fr 40px 1fr 1fr 180px;
          gap: 12px;
          align-items: center;
        }

        .input-box {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 10px 15px;
          background: #f8fafc;
          text-align: left;
        }

        .input-box label {
          display: block;
          font-size: 10px;
          font-weight: 800;
          color: #94a3b8;
          text-transform: uppercase;
          margin-bottom: 2px;
        }

        .input-box input {
          width: 100%;
          border: none;
          background: transparent;
          font-weight: 600;
          color: #1e293b;
          outline: none;
        }

        .swap-btn-wrap {
          display: flex;
          justify-content: center;
        }

        .swap-btn {
          width: 36px;
          height: 36px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #166070;
          transition: 0.3s;
        }

        .swap-btn:hover { background: #166070; color: white; transform: rotate(180deg); }

        .search-btn {
          height: 58px;
          background: #166070;
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 700;
          font-size: 16px;
          width: 100%;
          transition: 0.3s;
        }

        .search-btn:hover { background: #0b3d47; transform: translateY(-2px); }

        .trust-badges {
          display: flex;
          justify-content: center;
          gap: 40px;
          margin-top: 40px;
        }

        .badge-item {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #64748b;
          font-weight: 500;
        }

        .badge-item svg { color: #166070; font-size: 1.2rem; }

        @media (max-width: 991px) {
          .hero-section { padding: 60px 20px 100px 20px; }
          .hero-content h1 { font-size: 2rem; }
          .booking-grid { grid-template-columns: 1fr; gap: 10px; }
          .swap-btn-wrap { position: absolute; right: 30px; top: 110px; z-index: 5; }
          .tab-btn { flex: 1; text-align: center; }
          .trust-badges { flex-direction: column; gap: 15px; align-items: flex-start; padding-left: 20px; }
        }
      `}</style>

      <section className="hero-section">
        <div className="container hero-content2">
          <h1>India's No. 1 Bus Ticket Booking Sites</h1>
          <p>Over 2500+ bus operators and 1 Lakh+ routes across the country.</p>
        </div>
      </section>

      <div className="floating-booking-container">
        <div className="bus-form-card">
          {/* <div className="trip-type-tabs">
            <button className={`tab-btn ${tripType === "One Way" ? "active" : ""}`} onClick={() => setTripType("One Way")}>One Way</button>
            <button className={`tab-btn ${tripType === "Roundtrip" ? "active" : ""}`} onClick={() => setTripType("Roundtrip")}>Round Trip</button>
          </div> */}

          <div className="booking-grid">
            <div className="input-box position-relative">
              <label><FaMapMarkerAlt /> From</label>
              <input 
                type="text" 
                placeholder="Source City" 
                value={sourceSearch.query} 
                onChange={(e) => handleCitySearch(e.target.value, "source")} 
              />
              {sourceSearch.results.length > 0 && (
                <ul className="search-results-dropdown shadow-lg">
                  {sourceSearch.results.map(city => (
                    <li key={city.city_id} onClick={() => selectCity(city, "source")}>{city.name}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="swap-btn-wrap">
              <button className="swap-btn shadow-sm" onClick={swapCities}><HiArrowsRightLeft /></button>
            </div>

            <div className="input-box position-relative">
              <label><FaMapMarkerAlt /> To</label>
              <input 
                type="text" 
                placeholder="Destination City" 
                value={destSearch.query} 
                onChange={(e) => handleCitySearch(e.target.value, "dest")} 
              />
              {destSearch.results.length > 0 && (
                <ul className="search-results-dropdown shadow-lg">
                  {destSearch.results.map(city => (
                    <li key={city.city_id} onClick={() => selectCity(city, "dest")}>{city.name}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="input-box">
              <label><FaCalendarAlt /> Date</label>
              <input name="travelDate" placeholder="Travel Date" className="bus-date-input" readOnly />
            </div>

            <button className="search-btn" onClick={onSearchBuses}>
              <FaSearch className="me-2" /> SEARCH
            </button>
          </div>

          {tripType === "Roundtrip" && (
            <div className="mt-3 animate__animated animate__fadeIn">
              <div className="input-box" style={{maxWidth: '250px'}}>
                <label><FaCalendarAlt /> Return Date</label>
                <input name="returnDate" placeholder="Return Date" className="bus-date-input" readOnly />
              </div>
            </div>
          )}

          <div className="trust-badges d-none d-md-flex">
            <div className="badge-item"><FaShieldAlt /> 100% Secure Payments</div>
            <div className="badge-item"><FaTicketAlt /> M-Tickets Available</div>
            <div className="badge-item"><FaClock /> 24/7 Customer Support</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BusBookingHero;